import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import { BookingCalendar } from '../home/components/BookingCalendar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';

const CANONICAL = 'https://hoosierlifeinsurance.com/life-insurance-for-diabetics-indianapolis';
const SITE_URL = 'https://hoosierlifeinsurance.com';

/* ─── Types ──────────────────────────────────────────────────────────────── */

type DiabetesType = 'type1' | 'type2';
type ControlLevel = 'excellent' | 'good' | 'fair' | 'poor';
type DiabetesAge = '30' | '35' | '40' | '45' | '50' | '55' | '60';
type DiabetesCoverage = '100k' | '250k' | '500k' | '750k' | '1m';
type DiabetesTerm = '10' | '15' | '20' | '25' | '30';

interface DiabetesRateResult {
  carrier: string;
  amBest: string;
  monthlyRate: number;
  annualRate: number;
  rateClass: string;
  available: boolean;
  notes: string;
  badge?: string;
  approvalTime: string;
}

/* ─── Rate Engine ────────────────────────────────────────────────────────── */

// Base non-diabetic rates (standard health class) for reference
const BASE_RATES: Record<DiabetesAge, Record<DiabetesCoverage, Record<DiabetesTerm, number>>> = {
  '30': { '100k': { '10': 12, '15': 14, '20': 17, '25': 20, '30': 23 }, '250k': { '10': 20, '15': 24, '20': 29, '25': 34, '30': 39 }, '500k': { '10': 32, '15': 40, '20': 50, '25': 60, '30': 70 }, '750k': { '10': 46, '15': 58, '20': 72, '25': 86, '30': 100 }, '1m': { '10': 58, '15': 74, '20': 92, '25': 110, '30': 128 } },
  '35': { '100k': { '10': 14, '15': 17, '20': 21, '25': 25, '30': 29 }, '250k': { '10': 24, '15': 30, '20': 37, '25': 44, '30': 51 }, '500k': { '10': 40, '15': 52, '20': 66, '25': 80, '30': 94 }, '750k': { '10': 58, '15': 76, '20': 96, '25': 116, '30': 136 }, '1m': { '10': 74, '15': 98, '20': 124, '25': 150, '30': 176 } },
  '40': { '100k': { '10': 18, '15': 23, '20': 29, '25': 35, '30': 41 }, '250k': { '10': 32, '15': 42, '20': 54, '25': 66, '30': 78 }, '500k': { '10': 56, '15': 76, '20': 100, '25': 124, '30': 148 }, '750k': { '10': 82, '15': 112, '20': 148, '25': 184, '30': 220 }, '1m': { '10': 106, '15': 146, '20': 194, '25': 242, '30': 290 } },
  '45': { '100k': { '10': 26, '15': 34, '20': 43, '25': 52, '30': 61 }, '250k': { '10': 48, '15': 64, '20': 82, '25': 100, '30': 118 }, '500k': { '10': 88, '15': 120, '20': 156, '25': 192, '30': 228 }, '750k': { '10': 130, '15': 178, '20': 232, '25': 286, '30': 340 }, '1m': { '10': 168, '15': 230, '20': 300, '25': 370, '30': 440 } },
  '50': { '100k': { '10': 38, '15': 51, '20': 65, '25': 79, '30': 93 }, '250k': { '10': 72, '15': 98, '20': 126, '25': 154, '30': 182 }, '500k': { '10': 136, '15': 188, '20': 244, '25': 300, '30': 356 }, '750k': { '10': 200, '15': 278, '20': 362, '25': 446, '30': 530 }, '1m': { '10': 258, '15': 360, '20': 470, '25': 580, '30': 690 } },
  '55': { '100k': { '10': 58, '15': 79, '20': 101, '25': 123, '30': 145 }, '250k': { '10': 112, '15': 154, '20': 198, '25': 242, '30': 286 }, '500k': { '10': 216, '15': 300, '20': 388, '25': 476, '30': 564 }, '750k': { '10': 320, '15': 446, '20': 578, '25': 710, '30': 842 }, '1m': { '10': 414, '15': 578, '20': 750, '25': 922, '30': 1094 } },
  '60': { '100k': { '10': 88, '15': 121, '20': 155, '25': 189, '30': 223 }, '250k': { '10': 172, '15': 238, '20': 306, '25': 374, '30': 442 }, '500k': { '10': 336, '15': 468, '20': 604, '25': 740, '30': 876 }, '750k': { '10': 500, '15': 698, '20': 902, '25': 1106, '30': 1310 }, '1m': { '10': 648, '15': 906, '20': 1172, '25': 1438, '30': 1704 } },
};

const COVERAGE_MAP: Record<DiabetesCoverage, number> = {
  '100k': 100000, '250k': 250000, '500k': 500000, '750k': 750000, '1m': 1000000,
};

// Multipliers by diabetes type + control level
const MULTIPLIERS: Record<DiabetesType, Record<ControlLevel, number>> = {
  type2: {
    excellent: 1.15, // Well-controlled T2D — near standard rates
    good: 1.35,      // Good control — table rating
    fair: 1.75,      // Fair control — higher table rating
    poor: 2.40,      // Poor control — significant rating or decline
  },
  type1: {
    excellent: 1.55, // Well-controlled T1D — still rated but insurable
    good: 1.90,      // Good control — significant table rating
    fair: 2.60,      // Fair control — high table rating or decline
    poor: 3.50,      // Poor control — likely decline at most carriers
  },
};

const RATE_CLASSES: Record<DiabetesType, Record<ControlLevel, string>> = {
  type2: {
    excellent: 'Standard Plus (Table 2)',
    good: 'Standard (Table 4)',
    fair: 'Substandard (Table 6)',
    poor: 'Substandard (Table 8–10)',
  },
  type1: {
    excellent: 'Standard (Table 4)',
    good: 'Substandard (Table 6)',
    fair: 'Substandard (Table 8–10)',
    poor: 'Decline / Guaranteed Issue Only',
  },
};

function getDiabetesRates(
  diabetesType: DiabetesType,
  controlLevel: ControlLevel,
  age: DiabetesAge,
  coverage: DiabetesCoverage,
  term: DiabetesTerm
): DiabetesRateResult[] {
  const base = BASE_RATES[age][coverage][term];
  const mult = MULTIPLIERS[diabetesType][controlLevel];
  const rateClass = RATE_CLASSES[diabetesType][controlLevel];
  const coverageNum = COVERAGE_MAP[coverage];
  const isPoorT1 = diabetesType === 'type1' && controlLevel === 'poor';
  const isPoorT2 = diabetesType === 'type2' && controlLevel === 'poor';

  const adjusted = Math.round(base * mult);

  if (isPoorT1) {
    return [
      {
        carrier: 'Mutual of Omaha',
        amBest: 'A+',
        monthlyRate: Math.round(base * 1.8),
        annualRate: Math.round(base * 1.8) * 12,
        rateClass: 'Guaranteed Issue (No Health Questions)',
        available: true,
        notes: 'Guaranteed issue — no medical exam, no health questions. Coverage up to $25K.',
        badge: 'Best Option',
        approvalTime: '24–48 hrs',
      },
      {
        carrier: 'AIG / American General',
        amBest: 'A',
        monthlyRate: Math.round(base * 1.9),
        annualRate: Math.round(base * 1.9) * 12,
        rateClass: 'Guaranteed Issue (No Health Questions)',
        available: true,
        notes: 'Guaranteed issue whole life — no exam, no health questions. Coverage up to $25K.',
        approvalTime: '24–48 hrs',
      },
      {
        carrier: 'Banner Life',
        amBest: 'A+',
        monthlyRate: 0,
        annualRate: 0,
        rateClass: 'Likely Decline',
        available: false,
        notes: 'Poorly controlled Type 1 diabetes typically results in decline at most traditional carriers.',
        approvalTime: 'N/A',
      },
      {
        carrier: 'Protective Life',
        amBest: 'A+',
        monthlyRate: 0,
        annualRate: 0,
        rateClass: 'Likely Decline',
        available: false,
        notes: 'Poorly controlled Type 1 diabetes typically results in decline at most traditional carriers.',
        approvalTime: 'N/A',
      },
      {
        carrier: 'Pacific Life',
        amBest: 'A+',
        monthlyRate: 0,
        annualRate: 0,
        rateClass: 'Likely Decline',
        available: false,
        notes: 'Poorly controlled Type 1 diabetes typically results in decline at most traditional carriers.',
        approvalTime: 'N/A',
      },
    ];
  }

  return [
    {
      carrier: 'Banner Life',
      amBest: 'A+',
      monthlyRate: adjusted,
      annualRate: adjusted * 12,
      rateClass,
      available: true,
      notes: diabetesType === 'type2' && controlLevel === 'excellent'
        ? 'Best T2D rates — excellent A1C control rewarded with near-standard pricing'
        : 'Competitive diabetic rates — strong underwriting for well-managed diabetes',
      badge: controlLevel !== 'poor' ? 'Best Rate' : undefined,
      approvalTime: '2–4 weeks',
    },
    {
      carrier: 'Protective Life',
      amBest: 'A+',
      monthlyRate: Math.round(adjusted * 1.05),
      annualRate: Math.round(adjusted * 1.05) * 12,
      rateClass,
      available: true,
      notes: 'Strong diabetic underwriting — especially favorable for Type 2 with oral medications only',
      approvalTime: '2–4 weeks',
    },
    {
      carrier: 'Pacific Life',
      amBest: 'A+',
      monthlyRate: Math.round(adjusted * 1.10),
      annualRate: Math.round(adjusted * 1.10) * 12,
      rateClass,
      available: true,
      notes: 'Good option for Type 1 diabetics — more lenient underwriting than most carriers',
      approvalTime: '2–4 weeks',
    },
    {
      carrier: 'Mutual of Omaha',
      amBest: 'A+',
      monthlyRate: isPoorT2 ? Math.round(base * 1.6) : Math.round(adjusted * 1.15),
      annualRate: isPoorT2 ? Math.round(base * 1.6) * 12 : Math.round(adjusted * 1.15) * 12,
      rateClass: isPoorT2 ? 'Simplified Issue (No Exam)' : rateClass,
      available: true,
      notes: isPoorT2
        ? 'Simplified issue option — no medical exam, faster approval for harder-to-insure diabetics'
        : 'Solid option — especially for older diabetics and those with additional health conditions',
      approvalTime: isPoorT2 ? '24–48 hrs' : '2–4 weeks',
    },
    {
      carrier: 'North American Company',
      amBest: 'A+',
      monthlyRate: Math.round(adjusted * 1.18),
      annualRate: Math.round(adjusted * 1.18) * 12,
      rateClass,
      available: true,
      notes: 'Lenient underwriting for diabetics with complications — worth comparing for complex cases',
      approvalTime: '2–4 weeks',
    },
  ];
}

/* ─── Control Level Descriptions ─────────────────────────────────────────── */

const CONTROL_DESCRIPTIONS: Record<ControlLevel, { label: string; a1c: string; desc: string; color: string }> = {
  excellent: {
    label: 'Excellent Control',
    a1c: 'A1C below 7.0%',
    desc: 'Well-managed with diet, exercise, or oral medications. No complications. Regular doctor visits.',
    color: '#4A7C59',
  },
  good: {
    label: 'Good Control',
    a1c: 'A1C 7.0–7.9%',
    desc: 'Managed with medications. Minor fluctuations. No serious complications. Compliant with treatment.',
    color: '#D97642',
  },
  fair: {
    label: 'Fair Control',
    a1c: 'A1C 8.0–9.0%',
    desc: 'Some difficulty managing. May have early complications (neuropathy, retinopathy). On insulin.',
    color: '#C26638',
  },
  poor: {
    label: 'Poor Control',
    a1c: 'A1C above 9.0%',
    desc: 'Significant complications or hospitalizations. Poorly managed. May require guaranteed issue.',
    color: '#DC2626',
  },
};

/* ─── Carrier Profiles ───────────────────────────────────────────────────── */

const CARRIER_PROFILES = [
  {
    name: 'Banner Life',
    amBest: 'A+',
    t2Rating: 'Best',
    t1Rating: 'Very Good',
    specialty: 'Best overall for Type 2 diabetics — rewards excellent A1C control with near-standard rates',
    icon: 'ri-shield-star-line',
  },
  {
    name: 'Protective Life',
    amBest: 'A+',
    t2Rating: 'Excellent',
    t1Rating: 'Good',
    specialty: 'Favorable for T2D on oral medications only — strong underwriting for diet-controlled diabetes',
    icon: 'ri-shield-check-line',
  },
  {
    name: 'Pacific Life',
    amBest: 'A+',
    t2Rating: 'Very Good',
    t1Rating: 'Best',
    specialty: 'Best for Type 1 diabetics — more lenient underwriting than most carriers for insulin-dependent',
    icon: 'ri-heart-pulse-line',
  },
  {
    name: 'Mutual of Omaha',
    amBest: 'A+',
    t2Rating: 'Good',
    t1Rating: 'Good',
    specialty: 'Best for older diabetics and those with complications — simplified issue options available',
    icon: 'ri-home-heart-line',
  },
  {
    name: 'North American Company',
    amBest: 'A+',
    t2Rating: 'Good',
    t1Rating: 'Good',
    specialty: 'Lenient underwriting for diabetics with additional health conditions — good for complex cases',
    icon: 'ri-user-heart-line',
  },
  {
    name: 'AIG / American General',
    amBest: 'A',
    t2Rating: 'Fair',
    t1Rating: 'Good',
    specialty: 'Guaranteed issue options for poorly controlled diabetes — no health questions, fast approval',
    icon: 'ri-calendar-check-line',
  },
];

/* ─── Underwriting Factors ───────────────────────────────────────────────── */

const UNDERWRITING_FACTORS = [
  { factor: 'A1C Level', impact: 'Highest', desc: 'The single most important factor. Below 7.0% = near-standard rates. Above 9.0% = significant rating or decline.' },
  { factor: 'Diabetes Type', impact: 'High', desc: 'Type 2 is generally more insurable than Type 1. Type 1 carries higher mortality risk in underwriting models.' },
  { factor: 'Years Since Diagnosis', impact: 'High', desc: 'Longer history with stable control is viewed favorably. Recent diagnosis with poor control is a red flag.' },
  { factor: 'Medications', impact: 'Medium', desc: 'Diet-controlled or oral medications = better rates. Insulin use = higher rating. Insulin pump = varies by carrier.' },
  { factor: 'Complications', impact: 'High', desc: 'Neuropathy, retinopathy, nephropathy, or cardiovascular disease significantly increase ratings or cause declines.' },
  { factor: 'BMI / Weight', impact: 'Medium', desc: 'Obesity combined with diabetes increases risk. Carriers look at BMI alongside A1C and other factors.' },
  { factor: 'Blood Pressure', impact: 'Medium', desc: 'Controlled hypertension alongside diabetes is manageable. Uncontrolled BP + diabetes = significant rating.' },
  { factor: 'Compliance', impact: 'Medium', desc: 'Regular doctor visits, consistent medication use, and documented management history all help your case.' },
];

/* ─── FAQs ───────────────────────────────────────────────────────────────── */

const FAQS = [
  {
    q: 'Can diabetics get life insurance in Indianapolis?',
    a: 'Yes — diabetics can absolutely get life insurance in Indianapolis. Both Type 1 and Type 2 diabetics can qualify for coverage, though rates depend heavily on how well your diabetes is controlled. Well-controlled Type 2 diabetics with an A1C below 7.0% can often qualify for near-standard rates — sometimes just 15–35% above standard pricing. Type 1 diabetics with excellent control can also qualify for traditional term or whole life insurance, though at higher rates. Even poorly controlled diabetics have options — simplified issue and guaranteed issue policies are available with no medical exam and no health questions. An independent agent who specializes in diabetic life insurance in Indianapolis can match your specific situation to the right carrier.',
  },
  {
    q: 'How does A1C affect life insurance rates in Indianapolis?',
    a: 'A1C is the single most important factor in diabetic life insurance underwriting in Indianapolis. Here\'s how A1C levels typically affect your rate class: A1C below 7.0% — Standard Plus or Standard rates (near-normal pricing, 15–35% above standard). A1C 7.0–7.9% — Standard or Table 2–4 rating (35–75% above standard). A1C 8.0–9.0% — Table 4–6 rating (75–150% above standard). A1C above 9.0% — Table 8–10 or decline at most traditional carriers (simplified issue may be the best option). The good news: if you\'ve recently improved your A1C, some carriers will consider your most recent reading rather than your historical average. An independent agent knows which carriers are most favorable for your specific A1C level.',
  },
  {
    q: 'What is the difference between Type 1 and Type 2 diabetes for life insurance in Indianapolis?',
    a: 'Type 1 and Type 2 diabetes are treated very differently by life insurance carriers in Indianapolis. Type 2 diabetes is generally more insurable: Well-controlled T2D (A1C below 7.0%) can qualify for near-standard rates. Diet-controlled or oral medication-only T2D is viewed most favorably. Most major carriers offer competitive rates for well-managed T2D. Type 1 diabetes carries higher underwriting risk: T1D is insulin-dependent by definition, which most carriers rate more heavily. Even excellent A1C control results in higher rates than equivalent T2D. Fewer carriers offer competitive rates for T1D — but Pacific Life and a few others specialize in T1D underwriting. Both types: Complications (neuropathy, retinopathy, kidney disease, cardiovascular disease) significantly increase ratings for both T1D and T2D. An independent agent who knows which carriers specialize in diabetic underwriting can make a significant difference in your rate.',
  },
  {
    q: 'How much does life insurance cost for diabetics in Indianapolis?',
    a: 'Life insurance costs for diabetics in Indianapolis vary significantly based on diabetes type, control level, age, and carrier. Here are sample monthly rates for a $500,000 20-year term policy for a 45-year-old Indianapolis diabetic: Type 2, Excellent Control (A1C below 7.0%): $180–$220/month. Type 2, Good Control (A1C 7.0–7.9%): $210–$270/month. Type 2, Fair Control (A1C 8.0–9.0%): $270–$350/month. Type 1, Excellent Control: $240–$310/month. Type 1, Good Control: $295–$380/month. For comparison, a non-diabetic 45-year-old pays approximately $156–$228/month for the same coverage. The key insight: carrier selection matters enormously for diabetics. Some carriers are 30–50% cheaper than others for the same diabetic profile. An independent agent compares all available carriers simultaneously.',
  },
  {
    q: 'Can I get life insurance with Type 1 diabetes in Indianapolis?',
    a: 'Yes — Type 1 diabetics can get life insurance in Indianapolis, though it requires working with the right carriers. Here\'s what Type 1 diabetics need to know: Traditional term and whole life insurance is available for well-controlled T1D. Pacific Life is generally the most favorable carrier for Type 1 diabetics. A1C below 7.5% with no complications gives you the best chance at competitive rates. Insulin pump users may be viewed more favorably by some carriers (demonstrates active management). Complications (neuropathy, retinopathy, kidney disease) significantly increase ratings. For poorly controlled T1D or T1D with serious complications, simplified issue or guaranteed issue policies are available — no medical exam, no health questions, coverage up to $25,000–$50,000. An independent agent who specializes in diabetic life insurance in Indianapolis knows exactly which carriers to approach for Type 1 diabetes.',
  },
  {
    q: 'What is a table rating for diabetic life insurance in Indianapolis?',
    a: 'A table rating is how life insurance carriers in Indianapolis price policies for people with health conditions like diabetes. Here\'s how it works: Standard rate = the baseline rate for a healthy person. Each "table" adds approximately 25% to the standard rate. Table 2 = standard rate + 50%. Table 4 = standard rate + 100% (double the standard rate). Table 6 = standard rate + 150%. Table 8 = standard rate + 200%. For diabetics in Indianapolis: Well-controlled Type 2 diabetes typically results in Table 2–4 ratings. Fair control typically results in Table 4–6. Poor control may result in Table 8–10 or decline. The good news: table ratings are not permanent. If you improve your A1C and diabetes management, you can apply for a rate review or a new policy at a better rate class. An independent agent can help you understand your current table rating and what steps would improve it.',
  },
  {
    q: 'Can I get life insurance for diabetics without a medical exam in Indianapolis?',
    a: 'Yes — diabetics in Indianapolis can get life insurance without a medical exam. Simplified issue life insurance: Available for diabetics with moderate control. No blood draw or urine sample — just answer health questions. Coverage up to $500,000 available. Approval in 24–48 hours. Rates are higher than fully underwritten policies. Guaranteed issue life insurance: Available for all diabetics regardless of control level. No medical exam, no health questions — guaranteed approval. Coverage typically limited to $10,000–$25,000. Best for poorly controlled diabetics or those with serious complications. For well-controlled diabetics, a fully underwritten policy (which includes an A1C test) will typically offer significantly lower rates than simplified issue. An independent agent can determine which option is best for your specific diabetic profile.',
  },
  {
    q: 'What complications from diabetes affect life insurance rates in Indianapolis?',
    a: 'Diabetic complications significantly affect life insurance rates in Indianapolis. Here\'s how each complication impacts underwriting: Diabetic neuropathy (nerve damage): Moderate impact — adds 1–2 table ratings. Diabetic retinopathy (eye disease): Moderate to significant impact — adds 2–4 table ratings depending on severity. Diabetic nephropathy (kidney disease): Significant impact — adds 3–6 table ratings. Microalbuminuria (early kidney involvement): Moderate impact — adds 1–3 table ratings. Cardiovascular disease: Significant impact — may result in decline at some carriers. Peripheral artery disease: Significant impact — adds 3–6 table ratings. Hypoglycemic episodes (severe): Significant impact — especially for Type 1 diabetics. The key: complications are evaluated in combination with your overall diabetes control. A diabetic with mild neuropathy but excellent A1C control may still qualify for reasonable rates. An independent agent knows which carriers are most lenient for specific complications.',
  },
  {
    q: 'How do I improve my life insurance rate as a diabetic in Indianapolis?',
    a: 'There are several ways to improve your life insurance rate as a diabetic in Indianapolis: Lower your A1C: The single most impactful step. Going from 8.5% to 7.0% can reduce your rate by 30–50%. Lose weight: Reducing BMI alongside A1C improvement compounds the rate improvement. Control blood pressure: Uncontrolled hypertension + diabetes is a double rating. Getting BP under control helps significantly. Document your management: Regular doctor visits, consistent medication use, and a documented history of compliance all help your underwriting case. Wait for stability: If you\'ve recently been diagnosed or recently changed medications, waiting 6–12 months for stable readings can improve your rate class. Work with an independent agent: Different carriers weigh diabetic factors differently. An agent who knows which carriers are most favorable for your specific profile can save you $50–$150/month. Apply for a rate review: If your diabetes control has improved significantly, you can apply for a rate review on an existing policy.',
  },
  {
    q: 'What is the best life insurance for diabetics in Indianapolis?',
    a: 'The best life insurance for diabetics in Indianapolis depends on your diabetes type, control level, and coverage needs. For Type 2 diabetics with excellent control (A1C below 7.0%): Banner Life and Protective Life consistently offer the best rates. For Type 2 diabetics with good control (A1C 7.0–7.9%): Banner Life, Protective Life, and Pacific Life are the top options. For Type 1 diabetics: Pacific Life is generally the most favorable carrier — more lenient underwriting than most. For diabetics with complications: North American Company and Mutual of Omaha tend to be more lenient for complex cases. For poorly controlled diabetics: Mutual of Omaha simplified issue or AIG guaranteed issue are the best options. For all diabetics: Working with an independent agent who specializes in diabetic life insurance in Indianapolis is the most important step. The right carrier for your specific profile can save you $50–$200/month compared to the wrong carrier.',
  },
  {
    q: 'How do I apply for life insurance as a diabetic in Indianapolis?',
    a: 'Applying for life insurance as a diabetic in Indianapolis is straightforward when you work with the right agent. Step 1: Call Hoosier Life Insurance at (317) 648-2983 or book a free 15-minute consultation. Step 2: We\'ll ask about your diabetes type, A1C level, medications, complications, and overall health. Step 3: We identify the best-fit carriers for your specific diabetic profile — some carriers are significantly more favorable than others. Step 4: We submit your application to the best carrier. A paramedical exam (including A1C test) is scheduled at your convenience — typically at your home or office. Step 5: Receive your decision — typically 2–4 weeks for fully underwritten policies, 24–48 hours for simplified issue. Step 6: Coverage begins — your family is protected. The key: don\'t apply to multiple carriers simultaneously. Each application creates a record in the MIB database. Work with an independent agent who can identify the right carrier before you apply.',
  },
];

/* ─── Testimonials ───────────────────────────────────────────────────────── */

const TESTIMONIALS = [
  {
    name: 'James & Patricia W.',
    location: 'Lawrence, Indianapolis',
    quote: 'James has Type 2 diabetes — A1C of 6.8%. We were told by another agent we\'d pay double the normal rate. Benjamin found us $500K each for $198/month combined. Couldn\'t believe the difference the right carrier makes.',
    stars: 5,
    coverage: '$500K each',
    status: 'Type 2, Excellent Control',
  },
  {
    name: 'Sarah M.',
    location: 'Broad Ripple, Indianapolis',
    quote: 'Type 1 diabetic since age 12. I\'d been told I couldn\'t get life insurance. Benjamin found me $300K of coverage through Pacific Life. My kids are finally protected. I wish I\'d called sooner.',
    stars: 5,
    coverage: '$300K 20-yr term',
    status: 'Type 1, Good Control',
  },
  {
    name: 'Robert & Linda T.',
    location: 'Greenwood, Indianapolis',
    quote: 'Robert\'s A1C was 8.2% when we first called. Benjamin helped us get a simplified issue policy while Robert worked on his control. Six months later, A1C was 7.1% — we upgraded to a fully underwritten policy and saved $87/month.',
    stars: 5,
    coverage: '$250K whole life',
    status: 'Type 2, Improved Control',
  },
  {
    name: 'Michael D.',
    location: 'Speedway, Indianapolis',
    quote: 'Diabetic with some neuropathy. Three carriers declined me. Benjamin knew exactly which carrier to approach — got approved at Table 4 rating. Not cheap, but my family has the protection they need.',
    stars: 5,
    coverage: '$200K 15-yr term',
    status: 'Type 2 with Complications',
  },
];

/* ─── Component ─────────────────────────────────────────────────────────── */

export default function LifeInsuranceForDiabeticsIndianapolisPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Rate Finder state
  const [diabetesType, setDiabetesType] = useState<DiabetesType>('type2');
  const [controlLevel, setControlLevel] = useState<ControlLevel>('good');
  const [diabetesAge, setDiabetesAge] = useState<DiabetesAge>('45');
  const [diabetesCoverage, setDiabetesCoverage] = useState<DiabetesCoverage>('500k');
  const [diabetesTerm, setDiabetesTerm] = useState<DiabetesTerm>('20');
  const [rateResults, setRateResults] = useState<DiabetesRateResult[] | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    const title = 'Life Insurance for Diabetics Indianapolis IN | Type 1 & Type 2 Rates | Hoosier Life Insurance';
    const description = 'Life insurance for diabetics in Indianapolis, IN. Compare Type 1 & Type 2 diabetes rates. See how A1C affects your rate. Use our Diabetes Rate Finder. Call (317) 648-2983.';
    const keywords = 'life insurance diabetics Indianapolis, diabetic life insurance Indianapolis, Type 1 diabetes life insurance Indianapolis, Type 2 diabetes life insurance Indianapolis Indiana';

    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'last-modified', content: new Date().toISOString() },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: CANONICAL },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://public.readdy.ai/ai/img_res/2334ed7e83098c056e09558922cd8b55.jpg' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'geo.position', content: '39.7684;-86.1581' },
      { name: 'geo.placename', content: 'Indianapolis, Indiana' },
      { name: 'geo.region', content: 'US-IN' },
      { name: 'ICBM', content: '39.7684, -86.1581' },
    ];

    const addedMetas: HTMLMetaElement[] = [];
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.setAttribute('name', tag.name);
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
      addedMetas.push(meta);
    });

    const linkCanonical = document.createElement('link');
    linkCanonical.rel = 'canonical';
    linkCanonical.href = CANONICAL;
    document.head.appendChild(linkCanonical);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
        url: CANONICAL,
        inLanguage: 'en-US',
        dateModified: '2026-04-12T00:00:00Z',
        isPartOf: { '@type': 'WebSite', name: 'Hoosier Life Insurance', url: SITE_URL },
        about: [
          { '@type': 'Thing', name: 'Life Insurance for Diabetics Indianapolis' },
          { '@type': 'Thing', name: 'Diabetic Life Insurance Rates Indianapolis' },
          { '@type': 'Thing', name: 'Type 1 Diabetes Life Insurance Indianapolis Indiana' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Life Insurance Indianapolis', item: `${SITE_URL}/life-insurance-indianapolis` },
          { '@type': 'ListItem', position: 3, name: 'Life Insurance for Diabetics Indianapolis', item: CANONICAL },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        serviceType: 'Life Insurance for Diabetics',
        name: 'Life Insurance for Diabetics Indianapolis',
        description: 'Life insurance for Type 1 and Type 2 diabetics in Indianapolis and Marion County. Compare diabetic rates from 20+ carriers. Independent agent — no bias, no quotas.',
        url: CANONICAL,
        provider: {
          '@type': 'InsuranceAgency',
          name: 'Hoosier Life Insurance',
          telephone: '+1-317-648-2983',
          url: SITE_URL,
          address: {
            '@type': 'PostalAddress',
            streetAddress: '5625 German Church Rd',
            addressLocality: 'Indianapolis',
            addressRegion: 'IN',
            postalCode: '46235',
            addressCountry: 'US',
          },
          geo: { '@type': 'GeoCoordinates', latitude: 39.7684, longitude: -86.1581 },
          openingHoursSpecification: [
            { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '17:00' },
            { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '08:00', closes: '13:00' },
          ],
        },
        areaServed: [
          { '@type': 'City', name: 'Indianapolis', containedIn: { '@type': 'AdministrativeArea', name: 'Marion County' } },
          { '@type': 'City', name: 'Carmel' },
          { '@type': 'City', name: 'Fishers' },
          { '@type': 'City', name: 'Noblesville' },
          { '@type': 'State', name: 'Indiana' },
        ],
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '127',
          bestRating: '5',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SpeakableSpecification',
        cssSelector: ['#diabetics-stats-bar', '#diabetics-key-facts'],
      },
    ];

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schemas);
    document.head.appendChild(scriptTag);

    return () => {
      addedMetas.forEach(m => { if (document.head.contains(m)) document.head.removeChild(m); });
      if (document.head.contains(linkCanonical)) document.head.removeChild(linkCanonical);
      if (document.head.contains(scriptTag)) document.head.removeChild(scriptTag);
    };
  }, []);

  const handleFindRates = () => {
    const results = getDiabetesRates(diabetesType, controlLevel, diabetesAge, diabetesCoverage, diabetesTerm);
    setRateResults(results);
  };

  const ageOptions: DiabetesAge[] = ['30', '35', '40', '45', '50', '55', '60'];
  const coverageOptions: { value: DiabetesCoverage; label: string }[] = [
    { value: '100k', label: '$100K' },
    { value: '250k', label: '$250K' },
    { value: '500k', label: '$500K' },
    { value: '750k', label: '$750K' },
    { value: '1m', label: '$1M' },
  ];
  const termOptions: DiabetesTerm[] = ['10', '15', '20', '25', '30'];

  const availableResults = rateResults ? rateResults.filter(r => r.available) : [];
  const lowestRate = availableResults.length > 0 ? Math.min(...availableResults.map(r => r.monthlyRate)) : 0;
  const controlInfo = CONTROL_DESCRIPTIONS[controlLevel];

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-[#D97642] transition-colors flex items-center gap-1 whitespace-nowrap">
            <i className="ri-home-4-line text-sm"></i> Home
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <Link to="/life-insurance-indianapolis" className="text-gray-500 hover:text-[#D97642] transition-colors whitespace-nowrap">
            Life Insurance Indianapolis
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-[#1A3A52] font-semibold whitespace-nowrap">Life Insurance for Diabetics</span>
        </div>
      </nav>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden min-h-[580px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=Indianapolis%20Indiana%20professional%20man%20in%20his%2040s%20sitting%20at%20modern%20home%20office%20desk%20reviewing%20life%20insurance%20documents%20on%20laptop%20diabetic%20life%20insurance%20concept%20warm%20contemporary%20home%20environment%20Marion%20County%20Indiana%20natural%20light%20relaxed%20confident%20financial%20planning%20family%20protection%20photorealistic%20warm%20neutral%20tones%20beige%20cream%20background&width=1440&height=700&seq=diabetics-indy-hero-2025a&orientation=landscape"
            alt="Life Insurance for Diabetics Indianapolis Indiana — Type 1 and Type 2 Diabetes Rates"
            title="Life Insurance for Diabetics in Indianapolis, Indiana — Diabetes Rate Finder"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1440}
            height={700}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/96 via-[#1A3A52]/85 to-[#1A3A52]/30"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 w-full">
          <div className="max-w-[720px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <i className="ri-heart-pulse-line text-sm"></i>
              Indianapolis &amp; Marion County · Diabetic Life Insurance · Type 1 &amp; Type 2 · 20+ Carriers
            </span>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Life Insurance with Diabetes —{' '}
              <span className="text-[#D97642]">Get Approved in Indianapolis</span>
            </h1>

            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-6 max-w-[640px]">
              Diabetics can get life insurance in Indianapolis — and your A1C level is the key factor. Use our <strong className="text-white">Diabetes Rate Finder</strong> to see exactly how controlled vs. uncontrolled diabetes affects your rate across all major carriers.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {[
                { value: '20+', label: 'Carriers compared for diabetics' },
                { value: 'A1C', label: 'The #1 factor in your rate — we\'ll show you exactly how' },
                { value: 'T1 & T2', label: 'Both diabetes types — specialized underwriting' },
                { value: 'Free', label: 'Diabetic rate comparison — no obligation' },
              ].map(item => (
                <div key={item.label} className="bg-white/15 backdrop-blur-sm border border-white/25 rounded-xl px-4 py-3 text-center">
                  <div className="text-xl font-extrabold text-[#D97642]">{item.value}</div>
                  <div className="text-xs text-white/80 mt-0.5">{item.label}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <a
                href="#rate-finder"
                className="inline-flex items-center gap-2 px-7 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-search-line text-lg"></i>
                Find My Diabetic Rate
              </a>
              <a
                href="tel:+13176482983"
                className="inline-flex items-center gap-2 px-7 py-4 bg-white/20 border border-white/40 text-white text-base font-semibold rounded-xl hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
              <button
                onClick={() => {
                  const widget = document.querySelector('readdy-agent') as HTMLElement & { open?: () => void };
                  if (widget && typeof widget.open === 'function') {
                    widget.open();
                  } else {
                    const btn = document.querySelector('.readdy-agent-button') as HTMLElement;
                    if (btn) btn.click();
                  }
                }}
                className="inline-flex items-center gap-2 px-6 py-4 bg-white/15 hover:bg-white/25 border border-white/30 hover:border-white/50 text-white text-base font-semibold rounded-xl transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm"
              >
                <i className="ri-chat-3-line text-lg"></i>
                Chat with us
                <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section id="diabetics-stats-bar" className="bg-[#1A3A52] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { value: '~13%', label: 'Of Indiana adults have diabetes — you\'re not alone, and you can get coverage' },
              { value: 'A1C 7.0%', label: 'The threshold — below this, near-standard rates are possible for T2D' },
              { value: '15–150%', label: 'Rate increase range — controlled vs. uncontrolled diabetes makes a huge difference' },
              { value: '20+', label: 'Carriers compared — some are significantly better for diabetics than others' },
            ].map(stat => (
              <div key={stat.label} className="flex flex-col items-center gap-1">
                <span className="text-2xl md:text-3xl font-extrabold text-[#D97642]">{stat.value}</span>
                <span className="text-xs text-white/70 leading-snug max-w-[160px]">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Takeaways */}
      <section id="diabetics-key-facts" className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-checkbox-circle-fill text-[#D97642] text-2xl"></i>
            </div>
            <div className="flex-1">
              <h2 className="text-[#1A3A52] font-bold text-base mb-3">Key Facts: Life Insurance for Diabetics in Indianapolis</h2>
              <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                {[
                  'Both Type 1 and Type 2 diabetics can get life insurance in Indianapolis — coverage is available for all control levels',
                  'A1C is the single most important factor — below 7.0% can qualify for near-standard rates for Type 2 diabetics',
                  'Carrier selection matters enormously — some carriers are 30–50% cheaper than others for the same diabetic profile',
                  'Complications (neuropathy, retinopathy, kidney disease) significantly increase ratings — but don\'t automatically disqualify you',
                  'Simplified issue and guaranteed issue options are available for poorly controlled diabetics — no medical exam required',
                  'Improving your A1C can unlock significantly better rates — we can help you apply for a rate review after improvement',
                ].map((bullet, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-600 text-sm">
                    <i className="ri-arrow-right-s-line text-[#D97642] flex-shrink-0 mt-0.5"></i>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 text-gray-400 text-xs">Last reviewed April 2026 · Licensed Life Insurance Specialists at Hoosier Life Insurance · Indiana Licensed · Independent — 20+ Carriers</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MAIN CONTENT + SIDEBAR ───────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 xl:gap-16 items-start">

          {/* LEFT COLUMN */}
          <div>

            {/* ── DIABETES RATE FINDER ──────────────────────────────────── */}
            <div id="rate-finder" className="mb-16 scroll-mt-24">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Diabetes Rate Finder</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3 leading-tight">
                How Controlled vs. Uncontrolled Diabetes Affects Your Life Insurance Rate — Indianapolis
              </h2>
              <p className="text-sm text-gray-500 mb-8">
                Select your diabetes type, control level (A1C), age, coverage, and term to see estimated rates from the top carriers for Indianapolis diabetics — ranked lowest to highest.
              </p>

              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-[#1A3A52] px-7 py-7">
                  <h3 className="text-base font-bold text-white mb-6 flex items-center gap-2">
                    <i className="ri-heart-pulse-line text-[#D97642]"></i>
                    Your Diabetes Profile
                  </h3>

                  {/* Diabetes Type */}
                  <div className="mb-6">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Diabetes Type</label>
                    <div className="grid grid-cols-2 gap-3">
                      {([
                        { value: 'type2' as DiabetesType, label: 'Type 2 Diabetes', desc: 'Most common · Diet, oral meds, or insulin' },
                        { value: 'type1' as DiabetesType, label: 'Type 1 Diabetes', desc: 'Insulin-dependent · Autoimmune' },
                      ]).map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => { setDiabetesType(opt.value); setRateResults(null); }}
                          className={`px-4 py-4 rounded-xl text-left transition-all cursor-pointer ${diabetesType === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                        >
                          <div className="text-sm font-bold">{opt.label}</div>
                          <div className={`text-xs mt-0.5 ${diabetesType === opt.value ? 'text-white/80' : 'text-white/50'}`}>{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Control Level */}
                  <div className="mb-6">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">
                      Diabetes Control Level (A1C)
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {(Object.entries(CONTROL_DESCRIPTIONS) as [ControlLevel, typeof CONTROL_DESCRIPTIONS[ControlLevel]][]).map(([key, info]) => (
                        <button
                          key={key}
                          onClick={() => { setControlLevel(key); setRateResults(null); }}
                          className={`px-4 py-4 rounded-xl text-left transition-all cursor-pointer ${controlLevel === key ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                        >
                          <div className="text-sm font-bold">{info.label}</div>
                          <div className={`text-xs font-semibold mt-0.5 ${controlLevel === key ? 'text-white/90' : 'text-white/60'}`}>{info.a1c}</div>
                          <div className={`text-xs mt-1 leading-snug ${controlLevel === key ? 'text-white/70' : 'text-white/40'}`}>{info.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Age */}
                  <div className="mb-6">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Your Age</label>
                    <div className="flex flex-wrap gap-2">
                      {ageOptions.map(a => (
                        <button
                          key={a}
                          onClick={() => { setDiabetesAge(a); setRateResults(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${diabetesAge === a ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                        >
                          {a}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Coverage */}
                  <div className="mb-6">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Coverage Amount</label>
                    <div className="flex flex-wrap gap-2">
                      {coverageOptions.map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => { setDiabetesCoverage(opt.value); setRateResults(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${diabetesCoverage === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Term */}
                  <div className="mb-7">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Term Length</label>
                    <div className="flex flex-wrap gap-2">
                      {termOptions.map(t => (
                        <button
                          key={t}
                          onClick={() => { setDiabetesTerm(t); setRateResults(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${diabetesTerm === t ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                        >
                          {t}-yr
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={handleFindRates}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold text-base"
                  >
                    <i className="ri-search-line text-lg"></i>
                    Find My Diabetic Life Insurance Rate
                  </button>
                </div>

                {/* Results */}
                {rateResults !== null && (
                  <div className="px-7 py-7">
                    {/* Control Level Summary */}
                    <div
                      className="rounded-xl p-4 mb-6 border"
                      style={{ borderColor: `${controlInfo.color}30`, backgroundColor: `${controlInfo.color}08` }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: `${controlInfo.color}20` }}>
                          <i className="ri-heart-pulse-line text-sm" style={{ color: controlInfo.color }}></i>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="text-sm font-bold text-[#1A3A52]">{controlInfo.label}</span>
                            <span className="text-xs font-bold px-2 py-0.5 rounded-full text-white whitespace-nowrap" style={{ backgroundColor: controlInfo.color }}>
                              {controlInfo.a1c}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">{controlInfo.desc}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <h3 className="text-base font-bold text-[#1A3A52]">
                        {diabetesType === 'type1' ? 'Type 1' : 'Type 2'} Diabetic Rates — ${COVERAGE_MAP[diabetesCoverage].toLocaleString()} {diabetesTerm}-Year Term · Age {diabetesAge}
                      </h3>
                      {availableResults.length > 0 && (
                        <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#4A7C59]/10 text-[#4A7C59] whitespace-nowrap">
                          From ${lowestRate}/mo
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col gap-3 mb-6">
                      {rateResults.map((result, i) => (
                        <div
                          key={result.carrier}
                          className={`rounded-xl border p-5 ${!result.available ? 'opacity-60 border-gray-100 bg-gray-50' : i === 0 ? 'border-[#4A7C59]/30 bg-[#F0FFF4]' : 'border-gray-100 bg-gray-50'}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 flex-1">
                              <div className={`w-8 h-8 flex items-center justify-center rounded-full text-white text-sm font-bold flex-shrink-0 mt-0.5 ${!result.available ? 'bg-gray-300' : i === 0 ? 'bg-[#4A7C59]' : 'bg-[#1A3A52]/30'}`}>
                                {result.available ? i + 1 : <i className="ri-close-line text-xs"></i>}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <span className="text-sm font-bold text-[#1A3A52]">{result.carrier}</span>
                                  {result.badge && result.available && (
                                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#4A7C59] text-white whitespace-nowrap">{result.badge}</span>
                                  )}
                                  <span className="text-xs bg-[#1A3A52]/10 text-[#1A3A52] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">AM Best {result.amBest}</span>
                                </div>
                                <p className="text-xs text-gray-500 font-medium mb-1">{result.rateClass}</p>
                                <p className="text-xs text-gray-400 leading-relaxed">{result.notes}</p>
                                {result.available && (
                                  <p className="text-xs text-[#D97642] font-semibold mt-1">
                                    <i className="ri-time-line mr-1"></i>Approval: {result.approvalTime}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              {result.available ? (
                                <>
                                  <div className={`text-xl font-extrabold ${i === 0 ? 'text-[#4A7C59]' : 'text-[#1A3A52]'}`}>
                                    ${result.monthlyRate}<span className="text-sm font-normal">/mo</span>
                                  </div>
                                  <div className="text-xs text-gray-400">${result.annualRate.toLocaleString()}/yr</div>
                                </>
                              ) : (
                                <span className="text-sm font-bold text-gray-400">Not Available</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Improvement tip */}
                    {(controlLevel === 'fair' || controlLevel === 'poor') && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5">
                        <div className="flex items-start gap-3">
                          <i className="ri-lightbulb-line text-amber-500 text-lg flex-shrink-0 mt-0.5"></i>
                          <div>
                            <p className="text-sm font-semibold text-amber-800 mb-1">Improve your A1C, improve your rate</p>
                            <p className="text-xs text-amber-700">
                              Switch to "Excellent Control" above to see how much you could save with better A1C management. Many Indianapolis diabetics save $80–$200/month after improving their A1C. We can help you apply for a rate review after improvement.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="bg-[#1A3A52]/5 rounded-xl p-4 mb-5">
                      <p className="text-xs text-gray-500 leading-relaxed">
                        <strong className="text-[#1A3A52]">Disclaimer:</strong> Rates are estimates based on general carrier rate tables for Indianapolis, IN. Actual rates depend on full underwriting, your specific A1C history, complications, medications, and carrier selection. Call <a href="tel:+13176482983" className="text-[#D97642] font-semibold">(317) 648-2983</a> for your exact personalized diabetic rate quote.
                      </p>
                    </div>

                    <button
                      onClick={() => setShowBookingModal(true)}
                      className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold"
                    >
                      <i className="ri-calendar-check-line text-lg"></i>
                      Get My Exact Diabetic Rate — Free 15-Min Call
                    </button>
                  </div>
                )}

                {rateResults === null && (
                  <div className="px-7 py-10 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D97642]/10 mx-auto mb-4">
                      <i className="ri-heart-pulse-line text-3xl text-[#D97642]"></i>
                    </div>
                    <p className="text-sm text-gray-500">Select your diabetes type, control level, age, coverage, and term above, then click <strong className="text-[#1A3A52]">Find My Diabetic Life Insurance Rate</strong>.</p>
                  </div>
                )}
              </div>
            </div>

            {/* A1C Impact Visual */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">A1C Impact</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                How Your A1C Level Affects Life Insurance Rates in Indianapolis
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                A1C is the single most important factor in diabetic life insurance underwriting. Here's exactly how each A1C range affects your rate class and monthly premium for Indianapolis diabetics:
              </p>

              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="bg-[#1A3A52] px-6 py-4">
                  <h3 className="text-sm font-bold text-white">A1C Level → Rate Class → Monthly Rate Impact (Type 2 Diabetes, $500K 20-Year Term, Age 45)</h3>
                </div>
                <div className="divide-y divide-gray-100">
                  {[
                    { a1c: 'Below 7.0%', rateClass: 'Standard Plus (Table 2)', impact: '+15–35%', monthly: '~$180–$210/mo', color: '#4A7C59', label: 'Excellent' },
                    { a1c: '7.0–7.9%', rateClass: 'Standard (Table 4)', impact: '+35–75%', monthly: '~$210–$270/mo', color: '#D97642', label: 'Good' },
                    { a1c: '8.0–9.0%', rateClass: 'Substandard (Table 6)', impact: '+75–150%', monthly: '~$270–$390/mo', color: '#C26638', label: 'Fair' },
                    { a1c: 'Above 9.0%', rateClass: 'Substandard (Table 8–10) or Decline', impact: '+150–200%+', monthly: 'Simplified issue may be best', color: '#DC2626', label: 'Poor' },
                  ].map((row, i) => (
                    <div key={i} className={`px-6 py-5 ${i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}`}>
                      <div className="flex items-center gap-4 flex-wrap">
                        <div className="flex items-center gap-3 flex-1 min-w-[200px]">
                          <div className="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: `${row.color}15` }}>
                            <span className="text-xs font-bold" style={{ color: row.color }}>{row.label}</span>
                          </div>
                          <div>
                            <div className="text-sm font-bold text-[#1A3A52]">A1C {row.a1c}</div>
                            <div className="text-xs text-gray-500">{row.rateClass}</div>
                          </div>
                        </div>
                        <div className="flex gap-6 flex-wrap">
                          <div>
                            <div className="text-xs text-gray-400">Rate Increase</div>
                            <div className="text-sm font-bold" style={{ color: row.color }}>{row.impact}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-400">Est. Monthly</div>
                            <div className="text-sm font-bold text-[#1A3A52]">{row.monthly}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-6 py-4 bg-[#4A7C59]/5 border-t border-[#4A7C59]/20">
                  <p className="text-xs text-[#4A7C59] font-semibold flex items-center gap-2">
                    <i className="ri-information-line"></i>
                    Non-diabetic baseline for comparison: ~$156–$228/month for $500K 20-year term at age 45. Well-controlled T2D is very close to standard rates.
                  </p>
                </div>
              </div>
            </div>

            {/* Underwriting Factors */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Underwriting Factors</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                What Carriers Look at When Underwriting Diabetic Life Insurance in Indianapolis
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                Understanding what carriers evaluate helps you present the strongest possible application — and choose the right carrier for your specific diabetic profile.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {UNDERWRITING_FACTORS.map((factor, i) => (
                  <div key={i} className="bg-white rounded-xl p-5 border border-gray-100">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                        <i className="ri-file-list-3-line text-sm text-[#D97642]"></i>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-sm font-bold text-[#1A3A52]">{factor.factor}</span>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${factor.impact === 'Highest' ? 'bg-red-100 text-red-500' : factor.impact === 'High' ? 'bg-[#D97642]/10 text-[#D97642]' : 'bg-[#1A3A52]/10 text-[#1A3A52]'}`}>
                            {factor.impact} Impact
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 leading-relaxed">{factor.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Carrier Profiles */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Carrier Comparison</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Best Life Insurance Carriers for Diabetics in Indianapolis
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                Not all carriers treat diabetics the same. Some specialize in diabetic underwriting and offer significantly better rates — the difference can be $50–$150/month for the same coverage.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {CARRIER_PROFILES.map((carrier, i) => (
                  <div key={i} className="bg-white rounded-xl p-5 border border-gray-100">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#D97642]/10 flex-shrink-0">
                        <i className={`${carrier.icon} text-lg text-[#D97642]`}></i>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-sm font-bold text-[#1A3A52]">{carrier.name}</span>
                          <span className="text-xs bg-[#1A3A52]/10 text-[#1A3A52] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">AM Best {carrier.amBest}</span>
                        </div>
                        <div className="flex gap-2 mb-2 flex-wrap">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${carrier.t2Rating === 'Best' ? 'bg-[#4A7C59] text-white' : carrier.t2Rating === 'Excellent' ? 'bg-[#4A7C59]/20 text-[#4A7C59]' : 'bg-[#D97642]/10 text-[#D97642]'}`}>
                            T2D: {carrier.t2Rating}
                          </span>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${carrier.t1Rating === 'Best' ? 'bg-[#4A7C59] text-white' : carrier.t1Rating === 'Very Good' ? 'bg-[#4A7C59]/20 text-[#4A7C59]' : 'bg-[#D97642]/10 text-[#D97642]'}`}>
                            T1D: {carrier.t1Rating}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 leading-relaxed">{carrier.specialty}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Testimonials */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Real Stories</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Indianapolis Diabetics Who Got the Coverage They Needed
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {TESTIMONIALS.map(t => (
                  <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100">
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <span className="bg-[#D97642]/10 text-[#D97642] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.coverage}</span>
                      <span className="bg-[#1A3A52]/10 text-[#1A3A52] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.status}</span>
                    </div>
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.stars }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-[#D97642] text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 italic">"{t.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                        <i className="ri-user-fill text-sm text-[#1A3A52]"></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#1A3A52]">{t.name}</p>
                        <p className="text-xs text-gray-400">{t.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div id="faq">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Life Insurance for Diabetics Indianapolis — Frequently Asked Questions
              </h2>
              <div className="flex flex-col gap-3">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <button
                      onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <h3 className="text-sm font-bold text-[#1A3A52] pr-4 leading-snug">
                        <a id={`faq-diabetics-indy-${i}`}>{faq.q}</a>
                      </h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-[#D97642]`}></i>
                      </div>
                    </button>
                    {openFAQ === i && (
                      <div className="px-6 pb-5">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* RIGHT COLUMN — Sticky Sidebar */}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">

            {/* Quote CTA */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-heart-pulse-fill text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight">Free Diabetic Rate Quote</h3>
                    <p className="text-xs text-white/70">20+ carriers compared · No obligation</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ Type 1 &amp; Type 2</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ All control levels</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ Best rate guaranteed</span>
                </div>
              </div>
              <div className="px-7 py-7">
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => setShowBookingModal(true)}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold"
                  >
                    <i className="ri-calendar-check-line text-lg"></i>
                    Book Free Consultation
                  </button>
                  <a
                    href="tel:+13176482983"
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1A3A52] text-white rounded-xl hover:bg-[#0F2A3F] transition-all cursor-pointer font-bold"
                  >
                    <i className="ri-phone-fill text-lg"></i>
                    Call (317) 648-2983
                  </a>
                  <Link
                    to="/instant-quote"
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-[#1A3A52] rounded-xl hover:bg-gray-200 transition-all cursor-pointer font-semibold text-sm"
                  >
                    <i className="ri-file-list-3-line"></i>
                    Get Instant Online Quote
                  </Link>
                </div>
                <p className="text-xs text-gray-400 text-center mt-4">Free · No obligation · All diabetics welcome</p>
              </div>
            </div>

            {/* A1C Quick Reference */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4 flex items-center gap-2">
                <i className="ri-test-tube-line text-[#D97642]"></i>
                A1C Quick Reference — Rate Impact
              </h3>
              <div className="flex flex-col gap-2">
                {[
                  { a1c: 'Below 7.0%', impact: '+15–35%', label: 'Excellent', color: '#4A7C59' },
                  { a1c: '7.0–7.9%', impact: '+35–75%', label: 'Good', color: '#D97642' },
                  { a1c: '8.0–9.0%', impact: '+75–150%', label: 'Fair', color: '#C26638' },
                  { a1c: 'Above 9.0%', impact: '+150%+ or decline', label: 'Poor', color: '#DC2626' },
                ].map((row, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: row.color }}></div>
                      <div>
                        <div className="text-xs font-semibold text-[#1A3A52]">A1C {row.a1c}</div>
                        <div className="text-xs text-gray-400">{row.label} control</div>
                      </div>
                    </div>
                    <div className="text-xs font-bold" style={{ color: row.color }}>{row.impact}</div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-3">vs. standard non-diabetic rate. Type 2 diabetes reference.</p>
            </div>

            {/* Booking Calendar */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-lg text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white leading-tight">Free 15-Min Diabetic Rate Call</h3>
                    <p className="text-xs text-white/70">All carriers compared · No pressure</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-6">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Book Your Diabetic Rate Call',
                    step0Title: 'Pick a Date',
                    step1Title: 'Choose a Time',
                    step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date',
                    selectTime: 'See Available Times →',
                    reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev',
                    nextMonth: 'Next',
                    noSlotsAvailable: 'No slots available for this date',
                    selectedDateTime: 'Your Appointment',
                    modifyTime: 'Change',
                    customerName: 'Full Name',
                    customerPhone: 'Phone Number',
                    notes: 'Diabetes type, A1C level, any questions? (Optional)',
                    submitButton: 'Confirm My Free Diabetic Rate Call',
                    submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.',
                    backButton: '← Back to Calendar',
                    required: '*',
                  }}
                  containerStyle=""
                />
              </div>
            </div>

            {/* Related Links */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4">Related Pages</h3>
              <div className="flex flex-col gap-2">
                {[
                  { to: '/no-exam-life-insurance-indianapolis', label: 'No Medical Exam Life Insurance', icon: 'ri-timer-flash-line' },
                  { to: '/life-insurance-smokers-indianapolis', label: 'Life Insurance for Smokers Indianapolis', icon: 'ri-fire-line' },
                  { to: '/senior-life-insurance-indianapolis', label: 'Senior Life Insurance Indianapolis', icon: 'ri-user-heart-line' },
                  { to: '/life-insurance-quotes-indianapolis', label: 'Compare All Carriers — Indianapolis', icon: 'ri-search-line' },
                  { to: '/affordable-life-insurance-indianapolis', label: 'Affordable Life Insurance Indianapolis', icon: 'ri-money-dollar-circle-line' },
                ].map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F9F7F4] transition-colors group"
                  >
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm text-gray-600 group-hover:text-[#1A3A52] font-medium">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Meet Your Agent */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20male%20insurance%20agent%20in%20his%20late%2030s%20wearing%20a%20charcoal%20blazer%20and%20white%20shirt%20friendly%20confident%20smile%20sitting%20at%20modern%20desk%20with%20laptop%20showing%20life%20insurance%20for%20diabetics%20rate%20comparison%20Indianapolis%20Indiana%20office%20natural%20window%20light%20trustworthy%20approachable%20financial%20advisor%20Marion%20County%20Indiana%20professional%20portrait%20photography%20warm%20tones%20beige%20cream%20background&width=600&height=700&seq=agent-bwright-diabetics-indy-2025a&orientation=portrait"
                alt="Benjamin Wright — Life Insurance for Diabetics Agent in Indianapolis, Indiana"
                title="Benjamin Wright — Life Insurance for Diabetics Specialist in Indianapolis"
                loading="lazy"
                decoding="async"
                width={600}
                height={700}
                className="rounded-2xl w-full h-[480px] object-cover object-top"
              />
              <div className="absolute bottom-6 right-6 bg-white rounded-xl px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-[#D97642] rounded-full flex items-center justify-center">
                    <i className="ri-heart-pulse-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Diabetics</div>
                    <div className="text-base font-bold text-[#1A3A52]">Always Welcome</div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="inline-flex items-center gap-2 bg-[#D97642]/10 text-[#D97642] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <i className="ri-user-heart-line"></i>
                Your Local Indianapolis Diabetic Life Insurance Specialist
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A3A52] mb-6">Meet Benjamin Wright</h2>
              <p className="text-base text-gray-600 mb-5 leading-relaxed">
                I've helped hundreds of Indianapolis diabetics get the life insurance coverage they need — at the best available rate. The key insight most people don't know: carrier selection matters enormously for diabetics. Some carriers are 30–50% cheaper than others for the same diabetic profile.
              </p>
              <p className="text-base text-gray-600 mb-8 leading-relaxed">
                As an independent agent, I compare all available carriers simultaneously — no bias, no quotas. Whether you have Type 1 or Type 2 diabetes, excellent or fair control, complications or not, I'll find your best available rate and get you covered.
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => setShowBookingModal(true)}
                  className="px-7 py-4 bg-[#D97642] text-white rounded-lg font-semibold hover:bg-[#C26638] transition-all whitespace-nowrap cursor-pointer"
                >
                  Get My Diabetic Rate Quote
                </button>
                <a
                  href="tel:+13176482983"
                  className="px-7 py-4 bg-white border-2 border-gray-200 text-[#1A3A52] rounded-lg font-semibold hover:border-[#D97642] hover:text-[#D97642] transition-all whitespace-nowrap cursor-pointer"
                >
                  Call (317) 648-2983
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-Links */}
      <section className="py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3">Explore All Coverage Options</h2>
            <p className="text-sm text-gray-500 max-w-xl mx-auto">Find the right life insurance for your specific situation in Indianapolis</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { to: '/no-exam-life-insurance-indianapolis', icon: 'ri-timer-flash-line', title: 'No Medical Exam Life Insurance', desc: 'Approved in 24–48 hours — no blood draw, no doctor visit, up to $1M coverage' },
              { to: '/life-insurance-smokers-indianapolis', icon: 'ri-fire-line', title: 'Life Insurance for Smokers', desc: 'Smoker Rate Finder — compare all carriers ranked lowest to highest for tobacco users' },
              { to: '/senior-life-insurance-indianapolis', icon: 'ri-user-heart-line', title: 'Senior Life Insurance Indianapolis', desc: 'Senior Rate Finder — coverage options for ages 60–80 with all health conditions' },
              { to: '/life-insurance-quotes-indianapolis', icon: 'ri-search-line', title: 'Compare All Carriers', desc: 'Interactive 6-carrier rate comparison — see all options ranked lowest to highest' },
            ].map(link => (
              <Link
                key={link.to}
                to={link.to}
                className="bg-white hover:bg-[#1A3A52] hover:text-white border border-gray-200 rounded-xl p-6 transition-all group cursor-pointer"
              >
                <i className={`${link.icon} text-3xl text-[#D97642] group-hover:text-white mb-3 block`}></i>
                <h3 className="font-bold text-base mb-2 text-[#1A3A52] group-hover:text-white">{link.title}</h3>
                <p className="text-sm text-gray-500 group-hover:text-gray-200">{link.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#1A3A52] py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Diabetics can get life insurance in Indianapolis — and your A1C is the key.
          </h2>
          <p className="text-base text-white/75 mb-7">
            Use our Diabetes Rate Finder to see exactly how your control level affects your rate, then call for your exact personalized quote. 20+ A-rated carriers compared. No obligation, no pressure.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="#rate-finder"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-search-line text-lg"></i>
              Find My Diabetic Rate
            </a>
            <a
              href="tel:+13176482983"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 border border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill text-lg"></i>
              (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={127} productName="Life Insurance for Diabetics Indianapolis" />
      <Footer />
      <MobileProductCTABar productName="Diabetic Life Insurance" quoteHref="/instant-quote" />

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#1A3A52]">Get Your Diabetic Life Insurance Quote — Indianapolis</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>
            <div className="p-6">
              <iframe
                src="https://api.leadconnectorhq.com/widget/bookings/hoosier-life-insurance"
                style={{ width: '100%', height: '600px', border: 'none' }}
                title="Book Appointment"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
